import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../app/store";
import { CacheItem } from "../config/cache";
import {
  setAgents,
  setCustomer,
  setCustomers,
  setStaff,
  setStaffs,
  setToken,
  setUser,
} from "../reducers/userReducer";
import { setCustomersReload, setStaffReload } from "../reducers/reloadReducer";
import {
  GetAgentsProps,
  GetOneStaffProps,
  GetStaffProps,
  GetUsersProps,
  GetUserProps,
  LoginProps,
  RegisterStaffProps,
  SuspendUserProps,
  UnsuspendUserProps,
  UpdateUserProps,
  UpdateUserTypeProps,
  UserSuspensionProps,
} from "declarations/userServices";
import {
  GetAgents,
  GetOneStaff,
  GetStaff,
  GetUser,
  GetUsers,
  Login,
  RegisterStaff,
  SuspendUser,
  UnsuspendUser,
  UpdateUser,
  UpdateUserType,
  UserSuspension,
} from "api/services/user";
import { useAsyncOperation } from "./useAsyncOperation";

export default function useUser() {
  const { execute } = useAsyncOperation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, agents, staffs, customers, customer } = useSelector(
    (state: RootState) => state.user
  );
  const { staffReload, customersReload } = useSelector(
    (state: RootState) => state.reload
  );

  const login = async ({ email, password, setLoading }: LoginProps) => {
    try {
      await execute(() => Login({ email, password, setLoading }), {
        successMessage: "Login Successful",

        onSuccess: (response) => {
          const { user, message, status, token } = response;
          
          if (status === 401) throw new Error("Login Failed. Please check your credentials.")
          if (status !== 200) throw new Error(message);
          
          dispatch(setUser(user));
          dispatch(setToken(token));
          
          CacheItem("user", user);
          CacheItem("token", token, true);
        },
      });
    } catch (error) {}
  };

  const registerStaff = async (props: RegisterStaffProps) => {
    try {
      await execute(() => RegisterStaff(props), {
        successMessage: "Staff Registered Successfully",
        errorMessage: "Staff Registration Failed",

        onSuccess: (response) => {
          const { staff, message, status } = response;

          if (status !== 201) throw new Error(message);

          dispatch(setStaffReload(true));
          navigate(-1);
        },
      });
    } catch (error) {}
  };

  const getAgents = async ({ setLoading }: GetAgentsProps) => {
    if (agents && agents.length > 0) return;
    try {
      await execute(() => GetAgents({ setLoading }), {
        errorMessage: "Fetching Agents Failed",

        onSuccess: (response) => {
          const { agents: loadedAgents, message, status } = response;
          if (status !== 200) throw new Error(message);

          dispatch(setAgents(loadedAgents));
        },
      });
    } catch (error) {}
  };

  const getStaff = async ({ setLoading }: GetStaffProps) => {
    if (staffs && staffs.length > 0 && !staffReload) return;
    try {
      await execute(() => GetStaff({ setLoading }), {
        errorMessage: "Fetching Staff Failed",

        onSuccess: (response) => {
          const { staff: loadedStaffs, message, status } = response;

          if (status !== 200) throw new Error(message);

          dispatch(setStaffs(loadedStaffs));
          dispatch(setStaffReload(false));
        },
      });
    } catch (error) {}
  };

  const getOneStaff = async ({ setLoading, id }: GetOneStaffProps) => {
    if (staffs && staffs.length > 0) return;
    try {
      await execute(() => GetOneStaff({ setLoading, id }), {
        errorMessage: "Fetching Staff Details Failed",

        onSuccess: (response) => {
          const { staff, message, status } = response;

          if (status !== 200) throw new Error(message);
          dispatch(setStaff(staff));
        },
      });
    } catch (error) {}
  };

  const getUsers = async (
    { setLoading, limit, offset, search }: GetUsersProps,
    {
      reload,
      local,
      more,
      callBack,
    }: {
      reload?: boolean;
      local?: boolean;
      more?: boolean;
      callBack?: (num: number) => void;
    }
  ) => {
    if (
      customers &&
      customers.length > 0 &&
      !customersReload &&
      !reload &&
      !more &&
      !local
    )
      return;
    try {
      const response = await execute(() =>
        GetUsers({ setLoading, limit, offset, search }), {
        errorMessage: "Fetching Users Failed",
      });
      const { users: loadedUsers, message, status, count } = response;

      if (status !== 200) throw new Error(message);
      if (callBack) callBack(count);

      let totalUsers = [...loadedUsers];

      if (local) return totalUsers;
      if (more) totalUsers = [...customers, ...loadedUsers];

      dispatch(setCustomers(totalUsers));
      dispatch(setCustomersReload(false));
    } catch (error) {}
  };

  const getUser = async ({ setLoading, id }: GetUserProps) => {
    if (customer?._id === id) return customer;
    let fetchedUser = null;
    try {
      await execute(() => GetUser({ setLoading, id }), {
        errorMessage: "Fetching User Failed",

        onSuccess: (response) => {
          const { user: loadedUser, message, status } = response;
          if (status !== 200) throw new Error(message);
          dispatch(setCustomer(loadedUser));
          fetchedUser = loadedUser;
        },
      });
    } catch (error) {}

    return fetchedUser;
  };

  const updateUser = async (query: UpdateUserProps) => {
    try {
      await execute(() => UpdateUser(query), {
        successMessage: "User Updated Successfully",
        errorMessage: "Updating User Failed",

        onSuccess: (response) => {
          const { user: loadedUser, message, status } = response;
          if (status !== 200) throw new Error(message);
          dispatch(setCustomer(loadedUser));
          dispatch(setCustomersReload(true));
          setTimeout(() => {
            navigate(-1);
          }, 200);
        },
      });
    } catch (error) {}
  };

  const updateUserType = async (query: UpdateUserTypeProps) => {
    try {
      await execute(() => UpdateUserType(query), {
        successMessage: "User Type Updated Successfully",
        errorMessage: "Updating User Type Failed",

        onSuccess: (response) => {
          const { user: loadedUser, message, status } = response;
          if (status !== 200) throw new Error(message);
          dispatch(setCustomer(loadedUser));
          dispatch(setCustomersReload(true));
          setTimeout(() => {
            navigate(-1);
          }, 200);
        },
  
      });
    } catch (error) {}
  };

  const staffSuspension = async ({
    setLoading,
    id,
    isActive,
  }: UserSuspensionProps) => {
    try {
      await execute(() => UserSuspension({ setLoading, id, isActive }), {
        successMessage: "Staff Suspension Updated",
        errorMessage: "Staff Suspension Failed",
        onSuccess: (response) => {
          const { user: loadedUser, message, status } = response;
          if (status !== 200) throw new Error(message);

          dispatch(setStaff(loadedUser));
          dispatch(setStaffReload(true));

          setTimeout(() => {
            navigate(-1);
          }, 200);
        },
      });
    } catch (error) {}
  };

  const userSuspension = async ({
    setLoading,
    id,
    isActive,
  }: UserSuspensionProps) => {
    try {
      await execute(() => UserSuspension({ setLoading, id, isActive }), {

        successMessage: "User Suspension Updated",
        errorMessage: "User Suspension Failed",

        onSuccess: (response) => {
          const { user: loadedUser, message, status } = response;

          if (status !== 200) throw new Error(message);

          dispatch(setCustomer(loadedUser));
          dispatch(setCustomersReload(true));
          setTimeout(() => {
            navigate(-1);
          }, 200);
        },
      });
    } catch (error) {}
  };

  const suspendUser = async ({
    setLoading,
    user: userToBeSuspended,
    date,
  }: SuspendUserProps) => {
    try {
      await execute(
        () => SuspendUser({ setLoading, user: userToBeSuspended, date }),
        {
          successMessage: "User Suspended",
          errorMessage: "Suspending User Failed",

          onSuccess: (response) => {
            const { message, status } = response;
            if (status !== 200) throw new Error(message);
            dispatch(setCustomersReload(true));
            setTimeout(() => {
              navigate(-1);
            }, 200);
          },
        }
      );
    } catch (error) {}
  };

  const unsuspendUser = async ({
    setLoading,
    user: userToBeSuspended,
  }: UnsuspendUserProps) => {
    try {
      await execute(
        () => UnsuspendUser({ setLoading, user: userToBeSuspended }),
        {
          successMessage: "User Unsuspended",
          errorMessage: "Unsuspending User Failed",

          onSuccess: (response) => {
            const { message, status } = response;

            if (status !== 200) throw new Error(message);
            dispatch(setCustomersReload(true));
            setTimeout(() => {
              navigate(-1);
            }, 200);
          },
        }
      );
    } catch (error) {}
  };

  return {
    login,
    registerStaff,
    getAgents,
    getStaff,
    getOneStaff,
    getUsers,
    getUser,
    updateUser,
    updateUserType,
    staffSuspension,
    userSuspension,
    suspendUser,
    unsuspendUser,
  };
}
